import React, { useRef } from 'react';
import {
  ColorInput,
  EnhancedSlider,
  GridDropdown,
  MaterialMapRow,
  ToggleCheckbox,
} from '../../utils/gridUtils';
import { BLENDING_OPTIONS, SIDE_OPTIONS } from '../../utils/ThreeJsUtils/materialPropertyEditor';

const MaterialPropertyField = ({
  descriptor,
  onChange,
  onTextureUpload,
  bottomPanelSelector,
}) => {
  const fileRef = useRef(null);

  if (!descriptor) return null;

  const { key, kind } = descriptor;
  const label =
    descriptor.label ||
    (key.startsWith('uniform:')
      ? key.replace('uniform:', 'uniform.')
      : key);

  if (kind === 'boolean') {
    return (
      <ToggleCheckbox
        label={label}
        material
        checked={!!descriptor.value}
        onToggle={() => onChange(key, { kind: 'boolean', data: !descriptor.value })}
      />
    );
  }

  if (kind === 'color') {
    return (
      <ColorInput
        label={label}
        color={descriptor.value}
        setColor={(hex) => onChange(key, { kind: 'color', data: hex })}
        bottomPanelSelector={bottomPanelSelector}
      />
    );
  }

  if (kind === 'number') {
    return (
      <EnhancedSlider
        toFix={3}
        label={label}
        value={descriptor.value ?? 0}
        onChange={(v) => onChange(key, { kind: 'number', data: v })}
        min={descriptor.min ?? 0}
        max={descriptor.max ?? 1}
        step={descriptor.step ?? 0.01}
        unit=""
      />
    );
  }

  if (kind === 'side') {
    return (
      <GridDropdown
        label={label}
        value={descriptor.value}
        onChange={(e) =>
          onChange(key, { kind: 'side', data: Number(e.target.value) })
        }
        options={SIDE_OPTIONS.map((o) => ({
          value: o.value,
          label: o.label,
        }))}
      />
    );
  }

  if (kind === 'blending') {
    return (
      <GridDropdown
        label={label}
        value={descriptor.value}
        onChange={(e) =>
          onChange(key, { kind: 'blending', data: Number(e.target.value) })
        }
        options={BLENDING_OPTIONS.map((o) => ({
          value: o.value,
          label: o.label,
        }))}
      />
    );
  }

  if (kind === 'vector2') {
    const { x, y } = descriptor.value || { x: 0, y: 0 };
    return (
      <>
        <EnhancedSlider
          toFix={3}
          label={`${label}.x`}
          value={x}
          onChange={(v) =>
            onChange(key, { kind: 'vector2', data: { x: v, y } })
          }
          min={-10}
          max={10}
          step={0.01}
          unit=""
        />
        <EnhancedSlider
          toFix={3}
          label={`${label}.y`}
          value={y}
          onChange={(v) =>
            onChange(key, { kind: 'vector2', data: { x, y: v } })
          }
          min={-10}
          max={10}
          step={0.01}
          unit=""
        />
      </>
    );
  }

  if (kind === 'vector3') {
    const vec = descriptor.value || { x: 0, y: 0, z: 0 };
    return (
      <>
        {['x', 'y', 'z'].map((axis) => (
          <EnhancedSlider
            key={axis}
            toFix={3}
            label={`${label}.${axis}`}
            value={vec[axis]}
            onChange={(v) =>
              onChange(key, {
                kind: 'vector3',
                data: { ...vec, [axis]: v },
              })
            }
            min={-10}
            max={10}
            step={0.01}
            unit=""
          />
        ))}
      </>
    );
  }

  if (kind === 'string') {
    return (
      <div className="mb-2 flex items-center gap-[3px] w-full">
        <span className="text-[10px] text-gray-400 flex-[8] truncate">{label}</span>
        <input
          type="text"
          className="flex-[16] px-2 py-1 text-xs bg-[#2B2C2F] border border-gray-700 rounded text-white"
          value={descriptor.value ?? ''}
          onChange={(e) =>
            onChange(key, { kind: 'string', data: e.target.value })
          }
        />
      </div>
    );
  }

  if (kind === 'texture') {
    const mapEnabled = descriptor.mapEnabled ?? false;
    const canToggle = descriptor.hasTexture ?? !!descriptor.previewUrl;

    return (
      <>
        <MaterialMapRow
          label={label}
          previewUrl={descriptor.previewUrl}
          texture={descriptor.texture}
          checked={mapEnabled}
          disabled={!canToggle && !descriptor.previewUrl}
          onToggle={() => {
            if (!canToggle && !descriptor.previewUrl) return;
            onChange(key, {
              kind: 'texture',
              visibility: !mapEnabled,
            });
          }}
          onThumbnailClick={() => fileRef.current?.click()}
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = (ev) => {
              onTextureUpload?.(key, ev.target.result);
              onChange(key, { kind: 'texture', data: ev.target.result });
            };
            reader.readAsDataURL(file);
            e.target.value = '';
          }}
        />
      </>
    );
  }

  return null;
};

export default React.memo(MaterialPropertyField);
